import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Array<{
    id: string;
    title: string;
    type: string;
    score: number;
    chunk_text: string;
  }>;
  thinking?: boolean;
}

export const Route = createFileRoute('/knowledge_/test')({
  component: KnowledgeTestPage,
});

function KnowledgeTestPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSources, setShowSources] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    // Add thinking indicator
    setMessages(prev => [...prev, { role: 'assistant', content: '', thinking: true }]);
    setLoading(true);

    try {
      // First, search for relevant knowledge
      const searchRes = await fetch('/api/v1/knowledge/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: userMessage, limit: 5 }),
      });
      
      const searchData = await searchRes.json();
      const sources = searchData.results || [];

      // Build context from sources
      const context = sources
        .map((s: { title: string; chunk_text: string }) => `[${s.title}]: ${s.chunk_text}`)
        .join('\n\n');

      // Call the AI with context
      const chatRes = await fetch('/api/v1/voice/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          sessionId: 'knowledge-test',
          context: context || undefined,
          systemPrompt: `You are an AI assistant helping test a knowledge base. Answer questions based ONLY on the provided context. If the context doesn't contain relevant information, say "I don't have information about that in my knowledge base." Be concise and helpful.`,
        }),
      });

      const chatData = await chatRes.json();
      
      // Replace thinking indicator with actual response
      setMessages(prev => {
        const newMessages = prev.slice(0, -1); // Remove thinking indicator
        return [...newMessages, {
          role: 'assistant',
          content: chatData.response || chatData.message || 'Sorry, I could not generate a response.',
          sources: sources.length > 0 ? sources : undefined,
        }];
      });
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => {
        const newMessages = prev.slice(0, -1);
        return [...newMessages, {
          role: 'assistant',
          content: 'Sorry, there was an error processing your question. Please try again.',
        }];
      });
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    "What materials are used in your products?",
    "What is your return policy?",
    "How should I care for Nulu fabric?",
    "What sizes are available?",
    "Tell me about the Align pants",
  ];

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-12rem)] flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-light mb-2" style={{ color: '#171717' }}>
          Test AI Educator
        </h1>
        <p className="text-sm" style={{ color: '#A9A9A9' }}>
          Ask questions to test what your AI knows. See which knowledge sources are used.
        </p>
      </div>

      {/* Chat Area */}
      <div 
        className="flex-1 bg-white border overflow-hidden flex flex-col"
        style={{ borderColor: 'rgba(169,169,169,0.2)' }}
      >
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(0,251,169,0.1)' }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00FBA9" strokeWidth="1.5">
                  <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="font-medium mb-2" style={{ color: '#171717' }}>
                Ask the AI Educator
              </p>
              <p className="text-sm mb-6 max-w-sm" style={{ color: '#A9A9A9' }}>
                Test your knowledge base by asking questions. You'll see which sources the AI uses to answer.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q)}
                    className="px-3 py-1.5 rounded-full text-xs border transition-colors hover:border-gray-400"
                    style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#666' }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-2' : ''}`}>
                  {msg.thinking ? (
                    <div 
                      className="px-4 py-3 rounded-2xl"
                      style={{ backgroundColor: '#FAFAFA' }}
                    >
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className="px-4 py-3 rounded-2xl"
                        style={{
                          backgroundColor: msg.role === 'user' ? '#171717' : '#FAFAFA',
                          color: msg.role === 'user' ? '#FFFFFF' : '#171717',
                        }}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      
                      {/* Sources indicator */}
                      {msg.sources && msg.sources.length > 0 && (
                        <button
                          onClick={() => setShowSources(showSources === `${idx}` ? null : `${idx}`)}
                          className="mt-2 flex items-center gap-2 text-xs transition-colors"
                          style={{ color: '#00FBA9' }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {msg.sources.length} source{msg.sources.length !== 1 ? 's' : ''} used
                          <svg 
                            width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            style={{ transform: showSources === `${idx}` ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                          >
                            <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                      
                      {/* Sources panel */}
                      {showSources === `${idx}` && msg.sources && (
                        <div 
                          className="mt-2 border overflow-hidden"
                          style={{ borderColor: 'rgba(169,169,169,0.2)' }}
                        >
                          {msg.sources.map((source, sIdx) => (
                            <div 
                              key={sIdx}
                              className="p-3 border-b last:border-b-0"
                              style={{ borderColor: 'rgba(169,169,169,0.1)' }}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span 
                                  className="px-2 py-0.5 rounded text-xs uppercase"
                                  style={{ backgroundColor: 'rgba(0,251,169,0.1)', color: '#00A672' }}
                                >
                                  {source.type}
                                </span>
                                <span className="text-xs font-medium" style={{ color: '#171717' }}>
                                  {source.title}
                                </span>
                                <span className="text-xs ml-auto" style={{ color: '#A9A9A9' }}>
                                  {(source.score * 100).toFixed(0)}% match
                                </span>
                              </div>
                              <p className="text-xs line-clamp-2" style={{ color: '#666' }}>
                                {source.chunk_text}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question to test your knowledge base..."
              disabled={loading}
              className="flex-1 px-4 py-3 border text-sm"
              style={{ borderColor: 'rgba(169,169,169,0.3)' }}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="px-6 py-3 rounded-full text-sm font-medium transition-colors"
              style={{
                backgroundColor: !input.trim() || loading ? '#E5E5E5' : '#00FBA9',
                color: !input.trim() || loading ? '#A9A9A9' : '#171717',
              }}
            >
              {loading ? 'Thinking...' : 'Ask'}
            </button>
          </div>
          <p className="text-xs mt-2 text-center" style={{ color: '#A9A9A9' }}>
            Responses are generated using your indexed knowledge. Add more content in the Knowledge Hub.
          </p>
        </form>
      </div>
    </div>
  );
}

